import React, { useRef, useState, useEffect, useMemo } from 'react';
import { 
  Grid, 
  GridColumn, 
  GridItemChangeEvent,
  GridCellProps,
  GridHandle,
  GridSelectionChangeEvent,
  GridHeaderSelectionChangeEvent,
  GridSortChangeEvent
} from "@progress/kendo-react-grid";
import { SelectDescriptor } from '@progress/kendo-react-data-tools';
import { orderBy, SortDescriptor } from '@progress/kendo-data-query';
import { Button } from '@progress/kendo-react-buttons';
import { NumericTextBox } from '@progress/kendo-react-inputs';
import { plusIcon, trashIcon } from '@progress/kendo-svg-icons';
import { enMessages } from '../../../../messages/en-US';
import { useLocalization } from '@progress/kendo-react-intl';
import { GreasePump, GreasePumpStatus } from '../../../../Models/Assets/AssetTemplateReadDto';

interface Props {
  greasePumps: GreasePump[];
  onGreasePumpsChange: (updatedPumps: GreasePump[]) => void;
  onSelectionChange?: (selectedGreasePumpId: string | null) => void;
  selectedGreasePumpId?: string | null;
  onAddGreasePump?: () => void;
  instructionText?: string;
  /** Edit template: delete sets status=Deleted (soft). Create template: delete removes from list (hard). */
  isEditTemplate?: boolean;
}

const DATA_ITEM_KEY = 'id';
const INITIAL_SORT: SortDescriptor[] = [{ field: 'name', dir: 'asc' }];

function isGreasePumpActive(pump: GreasePump): boolean {
  if (pump.status !== undefined) return pump.status === 'Active';
  return pump.isEnabled !== false;
}

function isGreasePumpNameDuplicate(newName: string, greasePumps: GreasePump[], excludePumpId: string): boolean {
  const normalized = (newName || '').trim().toLowerCase();
  if (!normalized) return false;
  return greasePumps.some(
    (p) => isGreasePumpActive(p) && p.id !== excludePumpId && (p.name || '').trim().toLowerCase() === normalized
  );
}

const GreasePumpsTableTemplateComponent: React.FC<Props> = ({
  greasePumps,
  onGreasePumpsChange,
  onSelectionChange,
  selectedGreasePumpId,
  onAddGreasePump,
  instructionText,
  isEditTemplate = false
}) => {
  const gridRef = useRef<GridHandle>(null);
  const [select, setSelect] = useState<SelectDescriptor>({});
  const [nameError, setNameError] = useState<string | null>(null);
  const [sort, setSort] = useState<SortDescriptor[]>(INITIAL_SORT);
  const [orderedRowKeys, setOrderedRowKeys] = useState<string[]>([]);
  const localizationService = useLocalization();
  const previousVisibleKeysSignatureRef = useRef<string>('');
  const previousSortSignatureRef = useRef<string>('');

  const activeGreasePumps = useMemo(() => greasePumps.filter(isGreasePumpActive), [greasePumps]);
  const activeGreasePumpKeysSignature = useMemo(
    () => activeGreasePumps.map((pump) => pump.id).join('|'),
    [activeGreasePumps]
  );

  useEffect(() => {
    const sortSignature = JSON.stringify(sort);
    const didSortChange = previousSortSignatureRef.current !== sortSignature;
    const didVisibleRowsChange = previousVisibleKeysSignatureRef.current !== activeGreasePumpKeysSignature;

    if (!didSortChange && !didVisibleRowsChange) {
      return;
    }

    const sortedGreasePumps = sort.length > 0 ? orderBy(activeGreasePumps, sort) : activeGreasePumps;
    setOrderedRowKeys(sortedGreasePumps.map((pump) => pump.id));
    previousSortSignatureRef.current = sortSignature;
    previousVisibleKeysSignatureRef.current = activeGreasePumpKeysSignature;
  }, [activeGreasePumpKeysSignature, activeGreasePumps, sort]);

  const editableGreasePumps = useMemo(() => {
    const pumpsById = new Map(activeGreasePumps.map((pump) => [pump.id, pump]));
    const orderedGreasePumps = orderedRowKeys.length > 0
      ? orderedRowKeys
        .map((pumpId) => pumpsById.get(pumpId))
        .filter((pump): pump is GreasePump => pump !== undefined)
      : (sort.length > 0 ? orderBy(activeGreasePumps, sort) : activeGreasePumps);

    return orderedGreasePumps.map(pump => {
      // Ensure channelNo is always a valid number, defaulting to 0
      // Important: explicitly set to 0 (not undefined/null) so Kendo Grid displays it
      let channelNo: number;
      if (pump.channelNo !== undefined && pump.channelNo !== null) {
        if (typeof pump.channelNo === 'number') {
          channelNo = pump.channelNo;
        } else {
          const parsed = parseInt(String(pump.channelNo), 10);
          channelNo = isNaN(parsed) ? 0 : parsed;
        }
      } else {
        channelNo = 0; // Explicitly set to 0
      }
      
      return { 
        ...pump, 
        inEdit: true,
        channelNo: channelNo
      };
    });
  }, [activeGreasePumps, orderedRowKeys, sort]);

  useEffect(() => {
    if (editableGreasePumps.length > 0 && Object.keys(select).length === 0) {
      setSelect({ [editableGreasePumps[0].id]: true });
    }
  }, [editableGreasePumps, select]);

  useEffect(() => {
    if (selectedGreasePumpId) {
      setSelect({ [selectedGreasePumpId]: true });
    }
  }, [selectedGreasePumpId]);

  const handleItemChange = (event: GridItemChangeEvent) => {
    if (!event.dataItem || !event.dataItem.id) {
      return;
    }

    // Ensure channelNo is always a number (default to 0 if empty or invalid)
    let value = event.value;
    if (event.field === 'channelNo') {
      const numValue = parseInt(value, 10);
      value = isNaN(numValue) ? 0 : numValue;
    }

    const newGreasePumps = greasePumps.map((pump: GreasePump) =>
      pump.id === event.dataItem.id ? { ...pump, [event.field!]: value } : pump
    );
    onGreasePumpsChange(newGreasePumps);

    if (event.field === 'name') {
      const newName = (event.value as string) ?? '';
      if (!newName.trim()) {
        setNameError(localizationService.toLanguageString('custom.uniqueName', enMessages.custom.uniqueName));
      } else if (isGreasePumpNameDuplicate(newName, newGreasePumps, event.dataItem.id)) {
        setNameError(localizationService.toLanguageString('custom.nameExistsInTemplate', 'This name already exists in this template. Names must be unique.'));
      } else {
        setNameError(null);
      }
    }
  };

  const handleSelectionChange = (event: GridSelectionChangeEvent) => {
    if (!event.dataItem || !event.dataItem.id) {
      return;
    }
    
    const newPumpId = event.dataItem.id;
    setSelect({ [newPumpId]: true });
    onSelectionChange?.(newPumpId);
  };

  const handleHeaderSelectionChange = (event: GridHeaderSelectionChangeEvent) => {
    if (event.nativeEvent.target.checked) {
      const allSelected: SelectDescriptor = {};
      activeGreasePumps.forEach(pump => {
        allSelected[pump.id] = true;
      });
      setSelect(allSelected);
    } else {
      setSelect({});
    }
  };

  const handleDelete = (dataItem: any) => {
    if (isEditTemplate) {
      const updatedGreasePumps = greasePumps.map(pump =>
        pump.id === dataItem.id ? { ...pump, status: 'Deleted' as GreasePumpStatus } : pump
      );
      onGreasePumpsChange(updatedGreasePumps);
      if (selectedGreasePumpId === dataItem.id) {
        const nextActive = orderBy(updatedGreasePumps.filter(isGreasePumpActive), sort)[0];
        onSelectionChange?.(nextActive?.id ?? null);
      }
    } else {
      const updatedGreasePumps = greasePumps.filter(pump => pump.id !== dataItem.id);
      onGreasePumpsChange(updatedGreasePumps);
      if (selectedGreasePumpId === dataItem.id && updatedGreasePumps.length > 0) {
        const nextActive = orderBy(updatedGreasePumps.filter(isGreasePumpActive), sort)[0];
        onSelectionChange?.(nextActive?.id ?? null);
      }
    }
  };

  const EmptyHeaderCell = () => <div></div>;

  // Custom numeric editor for channelNo field (similar to UnitsInStock example)
  const channelNoEditor = (props: GridCellProps) => {
    const { dataItem, field } = props;
    const value = dataItem[field!] !== undefined && dataItem[field!] !== null 
      ? Number(dataItem[field!]) 
      : 0;
    
    return (
      <td>
        <NumericTextBox
          value={value}
          min={0}
          max={255}
          format="n0"
          onChange={(e) => {
            const newValue = e.value !== null && e.value !== undefined ? e.value : 0;
            handleItemChange({
              dataItem: dataItem,
              field: field,
              value: newValue
            } as GridItemChangeEvent);
          }}
          style={{ width: "100%" }}
        />
      </td>
    );
  };

  const deleteCell = (props: GridCellProps) => {
    const { dataItem } = props;
    return (
      <td style={{ textAlign: 'center' }}>
        <Button
          type="button"
          themeColor="primary"
          svgIcon={trashIcon}
          title={localizationService.toLanguageString('custom.delete', enMessages.custom.delete)}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleDelete(dataItem);
          }}
        />
      </td>
    );
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex justify-between align-middle mb-2">
        <div>
          <div className="text-xl font-bold">
            {localizationService.toLanguageString('custom.greasePumps', enMessages.custom.greasePumps)}
          </div>
          {instructionText && (
            <div className="text-sm text-gray-600 mt-1">
              {instructionText}
            </div>
          )}
          {nameError && (
            <p className="text-xs text-red-600 mt-1 px-1" style={{ marginBottom: 0 }}>* {nameError}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {onAddGreasePump && (
            <Button 
              type="button"
              themeColor="primary"
              className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary"
              svgIcon={plusIcon}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onAddGreasePump();
              }}
            />
          )}
        </div>
      </div>
      <div className="flex-1 min-h-0 overscroll-contain checkbox-outline-soft-override">
        <Grid
          ref={gridRef}
          data={editableGreasePumps}
          style={{ height: '100%' }}
          dataItemKey={DATA_ITEM_KEY}
          onItemChange={handleItemChange}
          sortable={true}
          sort={sort}
          onSortChange={(event: GridSortChangeEvent) => setSort(event.sort)}
          editable={true}
          editField="inEdit"
          selectable={{ enabled: true, mode: 'single' }}
          select={select}
          onSelectionChange={handleSelectionChange}
          onHeaderSelectionChange={handleHeaderSelectionChange}
        >
          <GridColumn columnType="checkbox" headerCell={EmptyHeaderCell} />
          <GridColumn 
            field="name" 
            title={localizationService.toLanguageString('custom.name', enMessages.custom.name)}
          />
          <GridColumn 
            field="channelNo" 
            title={localizationService.toLanguageString('custom.channel', enMessages.custom.channel)}
            cells={{ data: channelNoEditor }}
            editable={false}
          />
          {onAddGreasePump && (
            <GridColumn
              title={localizationService.toLanguageString('custom.actions', enMessages.custom.actions)}
              width="100px"
              sortable={false}
              filterable={false}
              editable={false}
              cells={{ data: deleteCell }}
            />
          )}
        </Grid>
      </div>
    </div>
  );
};

export default GreasePumpsTableTemplateComponent;
